/**
 * Hook de LEITURA das notificações do agente.
 *
 * Fornece o snapshot da lista + contagem via cache TanStack Query.
 * Múltiplos consumidores compartilham o MESMO cache; navegar entre páginas
 * não dispara refetch enquanto o dado estiver dentro do staleTime.
 *
 * A subscription Realtime, toasts e entrega multi-canal foram movidos para
 * `NotificationsRealtimeProvider` (montado 1x no root do App). Isso garante:
 *  - 1 canal Realtime por sessão (não 1 por consumidor);
 *  - toast disparado 1x mesmo quando várias telas usam o hook;
 *  - novos INSERTs atualizam o cache via `setQueryData` (sem refetch).
 *
 * @version 2.0.0 - 2026-07-02 — Refatorado para TanStack Query + provider raiz.
 * @version 1.x - Histórico anterior baseado em useState/useEffect + subscription local.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useAgentNotifications');

/**
 * Interface para notificação de agente
 * Inclui suporte para menções em comentários de atividades
 */
export interface AgentNotification {
  id: string;
  type:
    | 'transfer_received'
    | 'auto_assigned'
    | 'department_transfer'
    | 'activity_mention'
    // v1.2.0: menção em mensagem privada (nota interna) do chat
    | 'chat_note_mention'
    | 'scheduling_booking'
    | 'contract_signed'
    | 'payment_received'
    | 'task_assigned'
    | 'task_comment_added'
    | 'task_comment_mention'
    | 'deal_seller_assigned'
    | 'calendar_shared'
    | 'calendar_permission_updated'
    | 'calendar_permission_revoked';
  conversation_id: string | null;
  title: string;
  message: string | null;
  metadata: {
    // Transferências
    from_agent_id?: string | null;
    from_agent_name?: string | null;
    contact_name?: string | null;
    contact_phone?: string | null;
    contact_id?: string | null;
    department_name?: string | null;
    department_id?: string | null;
    // Menções em atividades
    activity_id?: string | null;
    activity_title?: string | null;
    comment_id?: string | null;
    mention_id?: string | null;
    author_id?: string | null;
    author_name?: string | null;
    deal_id?: string | null;
    customer_id?: string | null;
    // Assinatura de contrato
    contract_id?: string | null;
    contract_title?: string | null;
    deal_title?: string | null;
    signer_name?: string | null;
    signer_role?: string | null;
    provider_slug?: string | null;
    // Recebimento de pagamento
    cobranca_id?: string | null;
    customer_name?: string | null;
    valor_pago?: number | null;
    tipo_cobranca?: string | null;
    // Tarefas
    task_id?: string | null;
    task_number?: number | null;
    task_title?: string | null;
    category_name?: string | null;
    priority?: string | null;
    assigner_id?: string | null;
    assigner_name?: string | null;
    // Atribuição de vendedor em negócios (CRM)
    deal_value?: number | null;
    currency?: string | null;
    pipeline_id?: string | null;
    phase_id?: string | null;
    is_reassignment?: boolean | null;
    // Compartilhamento/permissão de calendário
    calendar_id?: string | null;
    calendar_name?: string | null;
    role?: string | null;
    old_permission?: string | null;
    new_permission?: string | null;
    changed_by?: string | null;
  };
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

/**
 * Opções de configuração do hook (aceitas por compatibilidade — a subscription
 * Realtime é única e vive no provider raiz).
 */
interface UseAgentNotificationsOptions {
  /** @deprecated Toast é disparado pelo NotificationsRealtimeProvider. */
  enableToasts?: boolean;
  /** Limite de notificações a buscar (default: 20). */
  limit?: number;
  /** @deprecated Realtime é global. */
  enableRealtime?: boolean;
}

/** Chaves de cache — compartilhadas com o provider para setQueryData/invalidate. */
export const agentNotificationsListKey = (userId: string | undefined, limit: number) =>
  ['agent-notifications', 'list', userId ?? 'anon', limit] as const;
export const agentNotificationsUnreadCountKey = (userId: string | undefined) =>
  ['agent-notifications', 'unread-count', userId ?? 'anon'] as const;

/**
 * Mapeia a linha bruta retornada pela RPC/Realtime para o tipo do hook.
 * Extraído para poder ser reutilizado pelo provider ao inserir INSERTs no cache.
 */
export function mapRawToAgentNotification(raw: Record<string, unknown>): AgentNotification {
  return {
    id: raw.id as string,
    type: raw.type as AgentNotification['type'],
    title: raw.title as string,
    message: (raw.message as string | null) ?? null,
    is_read: (raw.is_read as boolean) ?? false,
    read_at: (raw.read_at as string | null) ?? null,
    created_at: raw.created_at as string,
    conversation_id: (raw.conversation_id as string | null) ?? null,
    metadata: (raw.metadata || {}) as AgentNotification['metadata'],
  };
}

export function useAgentNotifications(options: UseAgentNotificationsOptions = {}) {
  const { limit = 20 } = options;

  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const listKey = agentNotificationsListKey(user?.id, limit);
  const countKey = agentNotificationsUnreadCountKey(user?.id);

  // Lista de notificações — cache singleton por (user, limit).
  const {
    data: notifications = [],
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery<AgentNotification[]>({
    queryKey: listKey,
    enabled: !!user?.id,
    staleTime: 60_000,        // 1 min: dentro desse período, hits de cache
    gcTime: 15 * 60_000,      // 15 min em memória mesmo sem consumidores
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_my_notifications', {
        p_limit: limit,
      });
      if (error) {
        logger.error('Erro ao buscar notificações:', error);
        throw error;
      }
      return ((data as Record<string, unknown>[]) || []).map(mapRawToAgentNotification);
    },
  });

  // Contagem real de não lidas — cache singleton por usuário.
  const { data: unreadTotal = 0 } = useQuery<number>({
    queryKey: countKey,
    enabled: !!user?.id,
    staleTime: 60_000,
    gcTime: 15 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as never as (
        fn: string,
      ) => Promise<{ data: number | null; error: { message: string } | null }>)(
        'get_my_unread_notifications_count',
      );
      if (error) {
        logger.error('Erro ao buscar contagem de não lidas:', error);
        throw new Error(error.message);
      }
      return Number(data) || 0;
    },
  });

  /**
   * Marca notificações como lidas (individual ou em batch) com atualização
   * otimista do cache — sem refetch.
   */
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationIds: string | string[]) => {
      const ids = Array.isArray(notificationIds) ? notificationIds : [notificationIds];
      if (ids.length === 0) return { ids };
      const { error } = await supabase.rpc('mark_notifications_as_read', {
        p_notification_ids: ids,
        p_mark_all: false,
      });
      if (error) throw error;
      return { ids };
    },
    onMutate: async (notificationIds) => {
      const ids = Array.isArray(notificationIds) ? notificationIds : [notificationIds];
      await queryClient.cancelQueries({ queryKey: listKey });
      const previousList = queryClient.getQueryData<AgentNotification[]>(listKey);
      const previousCount = queryClient.getQueryData<number>(countKey);
      // Otimista: marca como lidas na lista + decrementa o contador.
      queryClient.setQueryData<AgentNotification[]>(listKey, (prev) =>
        (prev ?? []).map((n) =>
          ids.includes(n.id)
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n,
        ),
      );
      const toRead = (previousList ?? []).filter(
        (n) => ids.includes(n.id) && !n.is_read,
      ).length;
      queryClient.setQueryData<number>(countKey, (prev) =>
        Math.max(0, (prev ?? 0) - toRead),
      );
      return { previousList, previousCount };
    },
    onError: (err, _vars, ctx) => {
      logger.error('Erro inesperado ao marcar como lida:', err);
      if (ctx?.previousList !== undefined) {
        queryClient.setQueryData(listKey, ctx.previousList);
      }
      if (ctx?.previousCount !== undefined) {
        queryClient.setQueryData(countKey, ctx.previousCount);
      }
    },
    onSettled: () => {
      // Reconcilia contagem com o servidor (cobre IDs fora da lista paginada).
      queryClient.invalidateQueries({ queryKey: countKey });
    },
  });

  const markAsRead = useCallback(
    (notificationIds: string | string[]) => markAsReadMutation.mutate(notificationIds),
    [markAsReadMutation],
  );

  /** Marca todas as notificações como lidas. */
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('mark_notifications_as_read', {
        p_notification_ids: null,
        p_mark_all: true,
      });
      if (error) throw error;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previousList = queryClient.getQueryData<AgentNotification[]>(listKey);
      const previousCount = queryClient.getQueryData<number>(countKey);
      queryClient.setQueryData<AgentNotification[]>(listKey, (prev) =>
        (prev ?? []).map((n) => ({
          ...n,
          is_read: true,
          read_at: new Date().toISOString(),
        })),
      );
      queryClient.setQueryData<number>(countKey, 0);
      return { previousList, previousCount };
    },
    onError: (err, _vars, ctx) => {
      logger.error('Erro inesperado ao marcar todas como lidas:', err);
      if (ctx?.previousList !== undefined) {
        queryClient.setQueryData(listKey, ctx.previousList);
      }
      if (ctx?.previousCount !== undefined) {
        queryClient.setQueryData(countKey, ctx.previousCount);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: countKey });
    },
  });

  const markAllAsRead = useCallback(
    () => markAllAsReadMutation.mutate(),
    [markAllAsReadMutation],
  );

  /** Navega para a conversa da notificação e marca como lida. */
  const navigateToConversation = useCallback(
    (notification: AgentNotification) => {
      if (notification.conversation_id) {
        if (!notification.is_read) {
          markAsRead(notification.id);
        }
        navigate(`/tenant/chat?conversation=${notification.conversation_id}`);
      }
    },
    [navigate, markAsRead],
  );

  // Derivados para conveniência dos consumidores atuais.
  const derivedUnread = notifications.filter((n) => !n.is_read).length;
  const unreadCount = unreadTotal || derivedUnread;
  const unreadNotifications = notifications.filter((n) => !n.is_read);
  const readNotifications = notifications.filter((n) => n.is_read);
  const error = queryError instanceof Error ? queryError.message : null;

  return {
    /** Lista completa de notificações */
    notifications,
    /** Apenas notificações não lidas */
    unreadNotifications,
    /** Apenas notificações lidas */
    readNotifications,
    /** Contagem de não lidas (para badge) */
    unreadCount,
    /** Estado de carregamento */
    loading,
    /** Erro, se houver */
    error,
    /** Recarrega notificações do banco (força bypass do cache) */
    refetch,
    /** Marca notificações específicas como lidas */
    markAsRead,
    /** Marca todas como lidas */
    markAllAsRead,
    /** Navega para conversa da notificação */
    navigateToConversation,
  };
}
